// style_manager.js

/**
 * @fileoverview A class to manage styling of atoms, compounds, and proteins.
 *
 * @typedef {import('BMapsModel').AtomGroup} AtomGroup
 * @typedef {import('BMapsSrc/themes').ColorChain} ColorChain
 * @typedef {import('BMapsModel').Atom} Atom
  * @typedef {import('BMapsSrc/Visualizer3Dmol').LabelSpec} labelSpec
 *
 * @typedef {{
 *  color?: ColorChain[keyof ColorChain],
 *  colorAllAtoms?: boolean,
 *  molStyle?: MolStyles[keyof MolStyles],
 *  showLabel?: boolean,
 *  label?: string,
 *  labelSpec?: labelSpec,
 *  ribbonColor?: ColorChain[keyof ColorChain],
 *  reps?: string[],
 *  transparency?: number | {sticks?: number, spheres?: number, cartoon?: number, surface?: number},
 *  cartoonType?: string,
 *  scopedReps?: {mainchain?: string[], sidechain?: string[]},
 * }} AtomGroupStyle
 *
 * `reps` (B3 option B): a set of additive representations (names from RepNames) drawn for the
 * group instead of one MolStyle. A group has a rep set or a molStyle, never both: setting one
 * clears the other. `transparency` (RepSetTransparencies) and `cartoonType` (RepSetCartoonTypes,
 * B3 Stage 3) change how a rep set is drawn and are dropped with it; defaults are not stored.
 * `scopedReps` (Stage 3c, polymers) gives a chain's main chain and / or side chains a rep set of
 * their own (util/chain_scope); a part without one draws what the whole group does.
 *
 * @typedef {ColorChain[keyof ColorChain]} color
 *
 * @typedef MolStyles
 * @property {'default'} default
 * @property {'hidden'} hidden
 * @property {'wireframe'} wireframe
 * @property {'sticks'} sticks
 * @property {'ballandstick'} ballandstick
 * @property {'spacefill'} spacefill
 * @property {'ribbons'} ribbons
 * @property {'backbone'} backbone
 * @property {'cartoon'} cartoon
 * @property {'surface'} surface
 * @property {'tribbons'} tribbons
 * @property {'ghost'} ghost
 * @property {'invisible'} invisible
 */

export const DefaultMolStyleCode = 'default';
/** @type MolStyles */
export const MolStyles = {
    default: DefaultMolStyleCode,
    hidden: 'hidden',
    wireframe: 'wireframe',
    sticks: 'sticks',
    ballandstick: 'ballandstick',
    spacefill: 'spacefill',
    ribbons: 'ribbons',
    backbone: 'backbone',
    cartoon: 'cartoon',
    surface: 'surface',
    tribbons: 'tribbons',
    ghost: 'ghost',
    invisible: 'invisible',
};

/**
 * Representations a group rep set can hold (AtomGroupStyle.reps), in display order. Both viewers
 * draw lines / sticks / spheres / cartoon / ribbon; surface / mesh / dots only exist per atom in
 * PyMOL (3Dmol ignores them).
 */
export const RepNames = ['lines', 'sticks', 'spheres', 'cartoon', 'ribbon', 'surface', 'mesh', 'dots'];

const RepSetPrefix = 'reps:';

/** Transparency choices of a rep set (AtomGroupStyle.transparency). */
export const RepSetTransparencies = [0, 0.25, 0.5, 0.75];

/**
 * Cartoon types of a rep set (AtomGroupStyle.cartoonType), 'automatic' = PyMOL's default.
 * 'putty' = a tube whose width follows the B-factor (PyMOL; 3Dmol draws a plain tube).
 */
export const RepSetCartoonTypes = ['automatic', 'loop', 'tube', 'putty', 'rectangle', 'oval'];

/** The reps a rep set's transparency applies to, each on its own when given per rep. */
export const TransparentRepNames = ['sticks', 'spheres', 'cartoon', 'surface'];

/** @returns {boolean} whether `value` is a valid transparency of a rep set */
function isRepSetTransparency(value) {
    return typeof value === 'number' && value >= 0 && value < 1;
}

/**
 * A rep set's transparency in its canonical form: one number when every rep has the same (0 =
 * none), else {rep: value} with the non-zero ones. null if `value` is neither.
 * @param {*} value number, or {sticks?, spheres?, cartoon?, surface?}
 * @returns {number|Object<string, number>|null}
 */
export function normalizeTransparency(value) {
    if (isRepSetTransparency(value)) return value;
    if (!value || typeof value !== 'object') return null;
    const byRep = transparencyByRep(value);
    const values = new Set(Object.values(byRep));
    if (values.size === 1) return [...values][0];
    return Object.fromEntries(Object.entries(byRep).filter(([, t]) => t > 0));
}

/**
 * @param {number|object|undefined} transparency a rep set's (number: every rep)
 * @returns {{sticks: number, spheres: number, cartoon: number, surface: number}}
 */
export function transparencyByRep(transparency) {
    return Object.fromEntries(TransparentRepNames.map((rep) => {
        const t = transparency && typeof transparency === 'object' ? transparency[rep] : transparency;
        return [rep, isRepSetTransparency(t) ? t : 0];
    }));
}

/**
 * @param {*} reps
 * @returns {string[]|null} the valid names, deduplicated, in RepNames order; null if not an array
 */
export function normalizeReps(reps) {
    if (!Array.isArray(reps)) return null;
    return RepNames.filter((name) => reps.includes(name));
}

/**
 * The per-atom style value of a rep set: 'reps:' + the names in RepNames order joined by '+'
 * ('reps:' draws nothing), then '|t=<transparency>' ('|t=0.5', or per rep
 * '|t=sticks:0.5,cartoon:0.25') and '|ct=<cartoon type>' unless they are the defaults (so a
 * plain set reads 'reps:sticks+cartoon'). A string on purpose, like a MolStyle: the
 * backends compare styles with ===, batch atoms by style and use it as a map key, and
 * display_mgr's hidden / invisible checks keep working unchanged.
 * @param {string[]} reps
 * @param {{transparency?: number|object, cartoonType?: string}} [options]
 * @returns {string}
 */
export function repSetStyle(reps, { transparency=0, cartoonType='automatic' }={}) {
    let style = RepSetPrefix + (normalizeReps(reps) || []).join('+');
    const t = normalizeTransparency(transparency);
    if (typeof t === 'number' && t > 0) style += `|t=${t}`;
    if (t && typeof t === 'object') {
        style += `|t=${Object.entries(t).map(([rep, value]) => `${rep}:${value}`).join(',')}`;
    }
    if (cartoonType !== 'automatic' && RepSetCartoonTypes.includes(cartoonType)) style += `|ct=${cartoonType}`;
    return style;
}

/**
 * The drawing options of a rep-set style value (repSetStyle), defaults filled in.
 * @returns {{transparency: number|object, cartoonType: string}|null} null for a MolStyle;
 *     transparency as normalizeTransparency gives it (transparencyByRep expands it)
 */
export function repSetOptions(style) {
    if (!isRepSetStyle(style)) return null;
    const options = { transparency: 0, cartoonType: 'automatic' };
    for (const part of style.split('|').slice(1)) {
        const [key, value] = part.split('=');
        if (key === 't' && value.includes(':')) {
            const byRep = Object.fromEntries(value.split(',').map((entry) => {
                const [rep, t] = entry.split(':');
                return [rep, Number(t)];
            }));
            options.transparency = normalizeTransparency(byRep) ?? 0;
        } else if (key === 't' && isRepSetTransparency(Number(value))) {
            options.transparency = Number(value);
        }
        if (key === 'ct' && RepSetCartoonTypes.includes(value)) options.cartoonType = value;
    }
    return options;
}

/** @returns {boolean} whether a per-atom style value is a rep set (repSetStyle) */
export function isRepSetStyle(style) {
    return typeof style === 'string' && style.startsWith(RepSetPrefix);
}

/** @returns {string[]|null} the representations of a rep-set style value, or null for a MolStyle */
export function repsOfStyle(style) {
    if (!isRepSetStyle(style)) return null;
    return style.slice(RepSetPrefix.length).split('|')[0].split('+').filter(Boolean);
}

/**
 * The nearest single MolStyle to a rep set, for a viewer that cannot draw rep sets (both of ours
 * can: this is the documented fallback of the seam).
 * @param {string[]} reps
 * @returns {MolStyles[keyof MolStyles]}
 */
export function molStyleForReps(reps) {
    const has = (name) => reps.includes(name);
    if (has('spheres')) return has('sticks') ? MolStyles.ballandstick : MolStyles.spacefill;
    if (has('sticks')) return MolStyles.sticks;
    if (has('cartoon')) return MolStyles.cartoon;
    if (has('ribbon')) return MolStyles.ribbons;
    if (has('lines')) return MolStyles.wireframe;
    if (has('surface') || has('mesh') || has('dots')) return MolStyles.surface;
    return MolStyles.hidden;
}

/**
 * The representations a single MolStyle draws (for groups that have no rep set yet).
 * @param {string} style
 * @returns {string[]}
 */
export function repsForMolStyle(style) {
    const reps = repsOfStyle(style);
    if (reps) return reps;
    switch (style) {
        case MolStyles.wireframe: return ['lines'];
        case MolStyles.sticks:
        case MolStyles.ghost: return ['sticks'];
        case MolStyles.ballandstick: return ['sticks', 'spheres'];
        case MolStyles.spacefill: return ['spheres'];
        case MolStyles.cartoon:
        case MolStyles.tribbons: return ['cartoon'];
        case MolStyles.ribbons:
        case MolStyles.backbone: return ['ribbon'];
        default: return []; // hidden, invisible, surface (drawn apart from the atoms)
    }
}

/**
 * @param {*} value {mainchain?: string[], sidechain?: string[]}
 * @returns {{mainchain?: string[], sidechain?: string[]}|null} normalized; null if none is valid
 */
function normalizeScopedReps(value) {
    if (!value || typeof value !== 'object') return null;
    const scoped = {};
    for (const scope of ['mainchain', 'sidechain']) {
        const reps = normalizeReps(value[scope]);
        if (reps) scoped[scope] = reps;
    }
    return Object.keys(scoped).length > 0 ? scoped : null;
}

function cloneAtomGroupStyle(style={}) {
    const copy = { ...style };
    if (copy.labelSpec) copy.labelSpec = { ...copy.labelSpec };
    return copy;
}

export class StyleManager {
    static get DefaultProteinViewProteinStyle() { return MolStyles.cartoon; }
    static get DefaultLigandViewProteinStyle() { return MolStyles.wireframe; }
    static get DefaultLabelStyle() {
        return {
            fontSize: 18,
            type: 'background',
        };
    }

    constructor() {
        this.resetInternal();
    }

    resetInternal() {
        /** @type {MolStyles[keyof MolStyles]} */
        this.ProteinViewProtein = StyleManager.DefaultProteinViewProteinStyle;
        /** @type {MolStyles[keyof MolStyles]} */
        this.LigandViewProtein = StyleManager.DefaultLigandViewProteinStyle;
        /** @type {{type: 'print' | 'background', fontSize: number}} */
        this.Label = StyleManager.DefaultLabelStyle;
        /** @type {Map<Atom['uniqueID'], MolStyles[keyof MolStyles]>} */
        this.AtomStyleMap = new Map();
        /** @type {Map<Atom['uniqueID'], color>} */
        this.AtomColorMap = new Map();
        /** @type {Map<AtomGroup, AtomGroupStyle>} */
        this.AtomGroupStyleMap = new Map();
    }

    updateAtomGroupInternal(atomGroup, newStyle) {
        if (!newStyle || Object.values(newStyle).length === 0) {
            this.AtomGroupStyleMap.delete(atomGroup);
        } else {
            this.AtomGroupStyleMap.set(atomGroup, newStyle);
        }
    }

    static reset() {
        TheStyleManager.resetInternal();
    }

    static get ProteinViewProteinStyle() {
        return TheStyleManager.ProteinViewProtein;
    }

    static set ProteinViewProteinStyle(style) {
        TheStyleManager.ProteinViewProtein = style;
    }

    static get LigandViewProteinStyle() {
        return TheStyleManager.LigandViewProtein;
    }

    static set LigandViewProteinStyle(style) {
        TheStyleManager.LigandViewProtein = style;
    }

    static get LabelStyle() {
        return { ...TheStyleManager.Label };
    }

    static set LabelStyle(style) {
        TheStyleManager.Label = { ...style };
    }

    /**
     * @param {Atom[]} atoms
     * @param {MolStyles[keyof MolStyles]} style
     *
     * This function is primarily used by UserActions.SetCustomAtomsStyle() in DisplayCmds.js
     * consider using that one instead.
     */
    static setCustomAtomsStyle(atoms, style) {
        for (const atom of atoms) {
            TheStyleManager.AtomStyleMap.set(atom.uniqueID, style);
        }
    }

    /**
     * @param {Atom[]} atoms
     *
     * This function is primarily used by UserActions.RemoveCustomAtomsStyle() in DisplayCmds.js
     * consider using that one instead.
     */
    static removeCustomAtomsStyle(atoms) {
        for (const atom of atoms) {
            TheStyleManager.AtomStyleMap.delete(atom.uniqueID);
        }
    }

    /** @param {Atom} atom */
    static getCustomAtomStyle(atom) {
        return TheStyleManager.AtomStyleMap.get(atom.uniqueID);
    }

    /** @param {Atom[]} atoms @param {color} color */
    static setCustomAtomsColor(atoms, color) {
        for (const atom of atoms) {
            TheStyleManager.AtomColorMap.set(atom.uniqueID, color);
        }
    }

    /** @param {Atom[]} atoms */
    static removeCustomAtomsColor(atoms) {
        for (const atom of atoms) {
            TheStyleManager.AtomColorMap.delete(atom.uniqueID);
        }
    }

    /** @param {Atom} atom */
    static getCustomAtomColor(atom) {
        return TheStyleManager.AtomColorMap.get(atom.uniqueID);
    }

    /**
     * @param {AtomGroup} atomGroup
     * @param {AtomGroupStyle} style
     *
     * This function is primarily used by UserActions.SetCustomAtomGroupStyle() in DisplayCmds.js
     * consider using that one instead.
     */
    static setCustomAtomGroupStyle(atomGroup, style) {
        const newStyleObj = TheStyleManager.AtomGroupStyleMap.get(atomGroup) || {};
        // a rep set's drawing options go with it
        const dropRepSet = () => {
            delete newStyleObj.reps;
            delete newStyleObj.transparency;
            delete newStyleObj.cartoonType;
            delete newStyleObj.scopedReps;
        };

        for (const [styleType, value] of Object.entries(style)) {
            if (value === 'default') { // DefaultColorCode or DefaultMolStyleCode
                if (styleType === 'reps') dropRepSet();
                delete newStyleObj[styleType];
                // choosing the default single style is choosing a single style: no rep set either
                if (styleType === 'molStyle') dropRepSet();
            } else if (styleType === 'reps') {
                const reps = normalizeReps(value);
                if (reps) {
                    newStyleObj.reps = reps;
                    delete newStyleObj.molStyle; // a rep set replaces the single style
                }
            } else if (styleType === 'scopedReps') {
                const scoped = normalizeScopedReps(value);
                if (scoped) newStyleObj.scopedReps = scoped;
                else delete newStyleObj.scopedReps; // e.g. {} after the last scope was reset
            } else if (styleType === 'transparency') { // the default (0) is stored as no value
                const t = normalizeTransparency(value);
                if (t === 0) delete newStyleObj.transparency;
                else if (t !== null) newStyleObj.transparency = t;
            } else if (styleType === 'cartoonType') {
                if (value === 'automatic') delete newStyleObj.cartoonType;
                else if (RepSetCartoonTypes.includes(value)) newStyleObj.cartoonType = value;
            } else {
                newStyleObj[styleType] = styleType === 'labelSpec' ? { ...value } : value;
                if (styleType === 'molStyle') dropRepSet();
            }
        }
        TheStyleManager.updateAtomGroupInternal(atomGroup, newStyleObj);
    }

    /**
     * @param {AtomGroup} atomGroup
     * @param {keyof AtomGroupStyle | 'all'} [styleType]
     * styleType is an optional argument. If you want to delete all styles just pass in atomGroup.
     *
     * This function is primarily used by UserActions.RemoveCustomAtomGroupStyle() in DisplayCmds.js
     * consider using that one instead.
     */
    static removeCustomAtomGroupStyle(atomGroup, styleType='all') {
        let newStyleObj = {}; // will be deleted from map if empty
        if (styleType !== 'all') {
            newStyleObj = TheStyleManager.AtomGroupStyleMap.get(atomGroup) || {};
            delete newStyleObj[styleType];
        }
        TheStyleManager.updateAtomGroupInternal(atomGroup, newStyleObj);
    }

    /**
     * @param {AtomGroup} atomGroup
     * @returns {AtomGroupStyle}
     */
    static getCustomAtomGroupStyle(atomGroup) {
        return cloneAtomGroupStyle(TheStyleManager.AtomGroupStyleMap.get(atomGroup));
    }

    /**
     * Replace, rather than merge, one group's custom style. This is the exact-restore seam used
     * by appearance undo; normal UI changes should continue to call setCustomAtomGroupStyle.
     * @param {AtomGroup} atomGroup
     * @param {AtomGroupStyle} style
     */
    static replaceCustomAtomGroupStyle(atomGroup, style) {
        TheStyleManager.updateAtomGroupInternal(atomGroup, cloneAtomGroupStyle(style));
    }

    /**
     * @param {AtomGroup[]} atomGroups
     * @returns {Object.<string, AtomGroupStyle>}
     */
    static getAllAtomGroupStyles(atomGroups) {
        const AtomGroupsStyle = {};
        atomGroups
            .filter((atomGroup) => !!TheStyleManager.AtomGroupStyleMap.get(atomGroup))
            .forEach((atomGroup) => {
                AtomGroupsStyle[atomGroup.key] = cloneAtomGroupStyle(
                    TheStyleManager.AtomGroupStyleMap.get(atomGroup)
                );
            });
        return AtomGroupsStyle;
    }

    static getGlobalStyleState() {
        return {
            LigandViewProteinStyle: StyleManager.LigandViewProteinStyle,
            ProteinViewProteinStyle: StyleManager.ProteinViewProteinStyle,
            LabelStyle: { ...StyleManager.LabelStyle },
            // Can't easily include Custom Atom Map because it is based on
            // atomUniqueIds, which cannot be guaranteed.
        };
    }

    /**
     * @param {{
     *  ProteinViewProteinStyle?: MolStyles[keyof MolStyles]
     *  LigandViewProteinStyle?: MolStyles[keyof MolStyles]
     *  LabelStyle?: {fontSize: number, type: 'background' | 'print'}
     * }} obj
     *
     * This function is primarily used by UserActions.SetGlobalStyleState() in DisplayCmds.js
     * consider using that one instead.
     */
    static setGlobalStyleState(obj) {
        const { ProteinViewProteinStyle: proteinView, LigandViewProteinStyle: ligandView } = obj;
        if (proteinView) {
            StyleManager.ProteinViewProteinStyle = proteinView !== 'default' ? proteinView
                : StyleManager.DefaultProteinViewProteinStyle;
        }

        if (ligandView) {
            StyleManager.LigandViewProteinStyle = ligandView !== 'default' ? ligandView
                : StyleManager.DefaultLigandViewProteinStyle;
        }

        if (obj.LabelStyle) {
            StyleManager.LabelStyle = obj.LabelStyle;
        }
    }
}

const TheStyleManager = new StyleManager();
